import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import { CallButton, WhatsAppButton } from "@/components/CtaButtons";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.09]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-ink-950"
    >
      <motion.div className="absolute inset-0" style={reduce ? undefined : { y, scale }}>
        <picture>
          <source media="(min-width: 768px)" srcSet="/img/salihli-oto-cekici-hero-desktop.webp" type="image/webp" />
          <img
            src="/img/salihli-oto-cekici-hero-mobile.webp"
            alt="Salihli Oto Çekici — gece, amber ışıkları yanan kurtarıcıya yüklenen 4x4 pickup"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover object-[center_55%]"
          />
        </picture>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950/95 via-ink-950/55 to-ink-950/10" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-ink-950/40" aria-hidden="true" />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-32 pt-32 md:pb-36 md:pt-40">
        <p
          data-testid="hero-badge"
          className="fade-up inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-display text-[11px] font-bold uppercase tracking-[0.24em] text-white/90 backdrop-blur"
          style={{ animationDelay: "60ms" }}
        >
          <span className="pulse-dot h-2 w-2 rounded-full bg-brand" aria-hidden="true" />
          7/24 Yol Yardım
        </p>

        <h1
          data-testid="hero-title"
          className="mt-7 font-display text-[16vw] font-black uppercase leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl"
        >
          <span className="hero-line-mask">
            <span className="hero-line" style={{ animationDelay: "140ms" }}>
              Salihli
            </span>
          </span>
          <span className="hero-line-mask">
            <span className="hero-line" style={{ animationDelay: "260ms" }}>
              Oto <span className="text-brand">Çekici</span>
            </span>
          </span>
        </h1>

        <p
          className="fade-up mt-6 max-w-xl text-base font-semibold leading-relaxed text-white/85 md:text-lg"
          style={{ animationDelay: "420ms" }}
        >
          Binek araçtan ticari araca, traktörden iş makinesine kadar profesyonel oto kurtarma ve
          araç taşıma hizmeti.
        </p>

        <ul
          data-testid="hero-trust-list"
          className="fade-up mt-6 flex flex-wrap items-center gap-x-6 gap-y-2.5"
          style={{ animationDelay: "520ms" }}
        >
          {siteConfig.heroTrust.map((item) => (
            <li key={item} className="flex items-center gap-2 text-[13px] font-semibold text-white/80 md:text-sm">
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand" aria-hidden="true">
                <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
              </span>
              {item}
            </li>
          ))}
        </ul>

        <div
          className="fade-up mt-9 flex flex-col gap-3.5 sm:flex-row sm:items-center"
          style={{ animationDelay: "620ms" }}
        >
          <CallButton
            location="hero"
            sub={siteConfig.phone.display}
            className="w-full px-8 py-4 text-lg sm:w-auto"
            testid="hero-call-button"
          />
          <WhatsAppButton
            location="hero"
            sub="Konumunuzu gönderin, hızla yola çıkalım."
            className="w-full px-8 py-4 text-base sm:w-auto"
            testid="hero-whatsapp-button"
          >
            WhatsApp'tan Konum Gönder
          </WhatsAppButton>
        </div>
      </div>

      <div
        className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 md:block"
        aria-hidden="true"
      >
        <div className="h-10 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
};
